import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IMobileEditorAppServices } from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';
import { IMobileTouchService } from '@src/slide/application/service/mobile-touch-service/mobile-touch-service.interface';

/**
 * 移动端 画布拖拽手势
 */
export class PlayerGestureHandler {
    public maxScrollX: number; // 最大右偏移
    public maxScrollY: number; // 最大上偏移
    public minScrollX: number; // 最小左偏移
    public minScrollY: number; // 最小下偏移
    private canvasDomRect: any; // 当前画布dom尺寸
    private canvasWrapperRef: HTMLDivElement | undefined; // 绑定的画布
    private cachedLastTouches: TouchList; // 缓存上一次touchmove触摸点
    private touchmoveCallbackList: Array<Function>; // touchmove回调队列
    private touchendCallbackList: Array<Function>; // touchend回调队列
    private cLeft: number; // 当前偏移x
    private cTop: number; // 当前偏移x
    private cachedCLeft: number; // 当前偏移x
    private cachedCTop: number; // 当前偏移y
    private canvasWrapperRect: DOMRect;
    private getCurrentScroll: any;
    private updateCurrentScroll: any;
    private playerMobileZoom: any;

    constructor(props) {
        this.playerMobileZoom = props.playerMobileZoom;
        this.getCurrentScroll = props.getCurrentScroll;
        this.updateCurrentScroll = props.updateCurrentScroll;
        this.touchmoveCallbackList = [];
        this.touchendCallbackList = [];
    }

    /**
     * 更新画布的逻辑尺寸、dom尺寸和当前的偏移量，由数据的钩子分发下来
     */
    public setCanvasSize = (domRect) => {
        if (!this.canvasWrapperRef) {
            return;
        }
        this.canvasWrapperRect = this.canvasWrapperRef.getBoundingClientRect();
        this.canvasDomRect = domRect;

        const { currentScrollX, currentScrollY } = this.getCurrentScroll();
        this.cLeft = currentScrollX;
        this.cTop = currentScrollY;
        this.setMaxScrollSize();
    };

    /**
     * 绑定画布事件
     */
    public bindCanvasWrapperRef = (domId) => {
        this.canvasWrapperRef = document.querySelector(domId) as HTMLDivElement;
        this.bindEvents();
    };

    public dispose = () => {
        if (this.canvasWrapperRef) {
            this.canvasWrapperRef.removeEventListener('touchstart', this.handleTouchStart);
        }
        this.touchmoveCallbackList = [];
        this.touchendCallbackList = [];
    };

    /**
     * 添加一个touchmove的回调
     * @private
     */
    public addTouchmoveCallback = (cb) => {
        this.touchmoveCallbackList.push(cb);
    };

    /**
     * 添加一个touchend的回调
     * @private
     */
    public addTouchendCallback = (cb) => {
        this.touchendCallbackList.push(cb);
    };

    /**
     * 移除一个touchmove的回调
     * @private
     */
    public removeTouchmoveCallback = (cb) => {
        this.touchmoveCallbackList = this.touchmoveCallbackList.filter(item => item !== cb);
    };

    /**
     * 绑定touchstart事件
     */
    private bindEvents = () => {
        if (this.canvasWrapperRef) {
            this.canvasWrapperRef.addEventListener('touchstart', this.handleTouchStart);
        }
    };

    /**
     * 根据当前touch获取与上一次touch的位置偏移
     * @param e
     */
    private getTouchDiffWithLastTouch = (e: TouchEvent) => {
        const { pageX, pageY } = e.changedTouches[0];
        return {
            offsetX: pageX - this.cachedLastTouches[0].pageX,
            offsetY: pageY - this.cachedLastTouches[0].pageY,
        };
    };

    /**
     * 设置最大可滚动距离
     */
    private setMaxScrollSize = () => {
        const { width, height } = this.canvasWrapperRect;
        const { width: canvasDomWidth, height: canvasDomHeight } = this.canvasDomRect;
        this.maxScrollX = 0;
        this.maxScrollY = 0;
        this.minScrollX = canvasDomWidth > width ? (width - canvasDomWidth) : 0;
        this.minScrollY = canvasDomHeight > height ? (height - canvasDomHeight) : 0;
    };

    /**
     * 拖动开始
     * @param e
     */
    private handleTouchStart = (e: TouchEvent) => {
        const { touches } = e;
        if (
            touches.length !== 1
            || this.playerMobileZoom.isZooming
            || this.getMobileTouchService().isTouchOnMediaElement
        ) return;
        this.cachedLastTouches = touches;
        this.cachedCLeft = this.cLeft;
        this.cachedCTop = this.cTop;
        document.body.addEventListener('touchmove', this.handleTouchMove, { passive: false });
        document.body.addEventListener('touchend', this.handleTouchEnd);
        document.body.addEventListener('touchcancel', this.handleTouchEnd);
    };

    /**
     * 拖动过程
     * @param e
     */
    private handleTouchMove = (e: TouchEvent) => {
        const { touches } = e;
        if (
            touches.length !== 1
            || this.playerMobileZoom.isZooming
            || this.getMobileTouchService().isTouchOnMediaElement
        ) {
            document.body.removeEventListener('touchmove', this.handleTouchMove);
            document.body.removeEventListener('touchend', this.handleTouchEnd);
            document.body.removeEventListener('touchcancel', this.handleTouchEnd);
            this.touchendCallbackList.forEach((cb) => {
                cb?.();
            });
            return;
        }
        e.preventDefault();
        const lastOffsetSize = this.getTouchDiffWithLastTouch(e);
        this.computeScrollOffset(lastOffsetSize);

        let { currentScrollX, currentScrollY } = this.getCurrentScroll();
        currentScrollX += this.cLeft - this.cachedCLeft;
        currentScrollY += this.cTop - this.cachedCTop;
        this.updateCurrentScroll(currentScrollX, currentScrollY);
        this.touchmoveCallbackList.forEach((cb) => {
            cb?.();
        });

        // 根据缓存的move计算完后，更新缓存
        this.cachedLastTouches = e.touches;
    };

    /**
     * 根据偏移量与最大可以偏移量计算实际可偏移距离
     * @param offsetSize
     * @private
     */
    private computeScrollOffset(offsetSize) {
        this.cachedCLeft = this.cLeft;
        this.cachedCTop = this.cTop;
        this.cLeft = this.cLeft + offsetSize.offsetX;
        this.cTop = this.cTop + offsetSize.offsetY;

        if (this.cLeft >= this.maxScrollX) {
            this.cLeft = this.maxScrollX;
        } else if (this.cLeft <= this.minScrollX) {
            this.cLeft = this.minScrollX;
        }

        if (this.cTop >= this.maxScrollY) {
            this.cTop = this.maxScrollY;
        } else if (this.cTop <= this.minScrollY) {
            this.cTop = this.minScrollY;
        }
    }

    /**
     * 拖动结束
     * @param e
     */
    private handleTouchEnd = (e) => {
        if (this.getMobileTouchService().isTouchOnMediaElement || this.playerMobileZoom.isZooming) {
            return;
        }

        document.body.removeEventListener('touchmove', this.handleTouchMove);
        document.body.removeEventListener('touchend', this.handleTouchEnd);
        document.body.removeEventListener('touchcancel', this.handleTouchEnd);

        this.touchendCallbackList.forEach((cb) => {
            cb?.();
        });
    };

    private getMobileTouchService(): IMobileTouchService {
        const appServices = AppServiceProvider.getInstance().getAppServices() as IMobileEditorAppServices;
        return appServices.viewableServiceLayer.mobileTouchService;
    }
}
